/* eslint-disable @typescript-eslint/naming-convention */
import { createAction, handleActions, Reducer } from 'redux-actions';
import { App } from '@api/types';
import { CustomUserPermissionTabs } from '@react/Pages/UsersPage/UserPermissionSidebar/AppAccess/CustomUserPermissions/utils';
import { TraceStatus } from '@redux/types';
import {
  AppRole,
  User,
  extractAppPermission,
  extractPartnerPermission,
  extractTrackerPermission,
  getInitialState,
  UserPermissionsScreen,
  UserPermissionsState,
  Feature,
} from './utils';

type UserPermissionsReducer<T = UserPermissionsState> = Reducer<UserPermissionsState, T>;

// Actions

export const USER_FETCH_STARTED = 'user/USER_FETCH_STARTED';
export const USER_FETCH_SUCCEED = 'user/USER_FETCH_SUCCEED';
export const USER_FETCH_FAILED = 'user/USER_FETCH_FAILED';
export const USER_CHANGE_ROLE = 'user/USER_CHANGE_ROLE';
export const TOGGLE_FEATURE = 'user/TOGGLE_FEATURE';
export const UPDATE_FEATURES = 'user/UPDATE_FEATURES';
export const NAVIGATE_TO = 'user/NAVIGATE_TO';
export const RESET_STATE = 'user/RESET_STATE';
export const SELECT_CURRENT_APP = 'user/SELECT_CURRENT_APP';
export const SELECT_CURRENT_TRACKER = 'user/SELECT_CURRENT_TRACKER';
export const SELECT_CURRENT_PARTNER = 'user/SELECT_CURRENT_PARTNER';
export const CHANGE_APP_PERMISSIONS = 'user/CHANGE_APP_PERMISSIONS';
export const CHANGE_TRACKER_PERMISSIONS = 'user/CHANGE_TRACKER_PERMISSIONS';
export const CHANGE_PARTNER_PERMISSIONS = 'user/CHANGE_PARTNER_PERMISSIONS';
export const DONT_UPDATE_BREADCRUMB = 'user/DONT_UPDATE_BREADCRUMB';
export const SAVE_USER_PERMISSIONS = 'user/SAVE_USER_PERMISSIONS';
export const SAVE_USER_PERMISSIONS_FAILED = 'user/SAVE_USER_PERMISSIONS_FAILED';
export const SAVE_USER_PERMISSIONS_SUCEED = 'user/SAVE_USER_PERMISSIONS_SUCEED';
export const SAVE_APP_PERMISSIONS = 'user/SAVE_APP_PERMISSIONS';
export const SAVE_TRACKER_PERMISSIONS = 'user/SAVE_TRACKER_PERMISSIONS';
export const SAVE_PARTNER_PERMISSIONS = 'user/SAVE_PARTNER_PERMISSIONS';
export const UPDATE_TRACKER_KEYWORDS = 'user/UPDATE_TRACKER_KEYWORDS';
export const CREATE_USER_STARTED = 'user/CREATE_USER_STARTED';
export const CREATE_USER_SUCCEED = 'user/CREATE_USER_SUCCEED';
export const CREATE_USER_FAILED = 'user/CREATE_USER_FAILED';
export const CLOSE_ALERT = 'user/CLOSE_ALERT';
export const UPDATE_EMAIL = 'user/UPDATE_EMAIL';
export const UPDATE_CURRENT_TAB = 'user/UPDATE_CURRENT_TAB';
export const ENABLE_APPLY_APP_PERMISSIONS = 'user/ENABLE_APPLY_APP_PERMISSIONS';
export const PARTNER_ENABLE_SELECTED = 'partner/PARTNER_ENABLE_SELECTED';
export const PARTNER_DISABLE_SELECTED = 'partner/PARTNER_DISABLE_SELECTED';
export const TRACKER_ENABLE_SELECTED = 'tracker/TRACKER_ENABLE_SELECTED';
export const TRACKER_DISABLE_SELECTED = 'tracker/TRACKER_DISABLE_SELECTED';

// Action creators

export const fetchUser = createAction(USER_FETCH_STARTED);
export const fetchUserSucceed = createAction(USER_FETCH_SUCCEED);
export const fetchUserFailed = createAction(USER_FETCH_FAILED);
export const changeUserRole = createAction(USER_CHANGE_ROLE);
export const toggleFeature = createAction(TOGGLE_FEATURE);
export const updateFeatures = createAction(UPDATE_FEATURES);
export const navigateTo = createAction(NAVIGATE_TO);
export const resetState = createAction(RESET_STATE);
export const selectCurrentApp = createAction(SELECT_CURRENT_APP);
export const selectCurrentTracker = createAction(SELECT_CURRENT_TRACKER);
export const selectCurrentPartner = createAction(SELECT_CURRENT_PARTNER);
export const changeAppPermissions = createAction(CHANGE_APP_PERMISSIONS);
export const changeTrackerPermissions = createAction(CHANGE_TRACKER_PERMISSIONS);
export const changePartnerPermissions = createAction(CHANGE_PARTNER_PERMISSIONS);
export const dontUpdateBreadcrumb = createAction(DONT_UPDATE_BREADCRUMB);
export const saveUserPermissions = createAction(SAVE_USER_PERMISSIONS);
export const saveUserPermissionsFailed = createAction(SAVE_USER_PERMISSIONS_FAILED);
export const saveUserPermissionsSuceed = createAction(SAVE_USER_PERMISSIONS_SUCEED);
export const saveAppPermissions = createAction(SAVE_APP_PERMISSIONS);
export const saveTrackerPermissions = createAction(SAVE_TRACKER_PERMISSIONS);
export const savePartnerPermissions = createAction(SAVE_PARTNER_PERMISSIONS);
export const updateTrackerKeywords = createAction(UPDATE_TRACKER_KEYWORDS);
export const createUser = createAction(CREATE_USER_STARTED);
export const createUserSuceed = createAction(CREATE_USER_SUCCEED);
export const createUserFailed = createAction(CREATE_USER_FAILED);
export const closeAlert = createAction(CLOSE_ALERT);
export const updateEmail = createAction(UPDATE_EMAIL);
export const updateCurrentTab = createAction(UPDATE_CURRENT_TAB);
export const enableApplyAppPermissions = createAction(ENABLE_APPLY_APP_PERMISSIONS);
export const enableSelectedPartners = createAction(PARTNER_ENABLE_SELECTED);
export const disableSelectedPartners = createAction(PARTNER_DISABLE_SELECTED);
export const enableSelectedTrackers = createAction(TRACKER_ENABLE_SELECTED);
export const disableSelectedTrackers = createAction(TRACKER_DISABLE_SELECTED);

// Reducers

const handleFetchUser: UserPermissionsReducer = state => ({
  ...state,
  isLoading: true,
});

const handleFetchUserSucceed: UserPermissionsReducer<User> = (state, { payload }) => ({
  ...state,
  isLoading: false,
  user: payload,
});

const handleFetchUserFailed: UserPermissionsReducer = state => ({
  ...state,
  isLoading: false,
  trace: {
    status: TraceStatus.Error,
    message: t('error.an-error-occurred.try-again-later'),
  },
});

const handleChangeUserRole: UserPermissionsReducer<string> = (state, { payload }) => {
  const user = {
    ...state.user,
    role: payload,
  };

  return {
    ...state,
    user,
    canApplyUserPermissions: true,
  };
};

const handleToggleFeature: UserPermissionsReducer<{ name: string; value: boolean }> = (state, { payload }) => {
  const featurePermission = state.user.local_roles.features.find(feature => feature.name === payload.name);

  const permission = {
    name: payload.name,
    role: payload.value ? 'reader' : 'none',
  };

  if (featurePermission) {
    featurePermission.role = permission.role;
  } else {
    state.user.local_roles.features.push(permission);
  }

  return {
    ...state,
    user: {
      ...state.user,
      local_roles: {
        ...state.user.local_roles,
        features: state.user.local_roles.features,
      },
    },
    canApplyUserPermissions: true,
  };
};

const handleUpdateFeatures: UserPermissionsReducer<Feature[]> = (state, { payload }) => ({
  ...state,
  user: {
    ...state.user,
    local_roles: {
      ...state.user.local_roles,
      features: payload,
    },
  },
});

const handleNavigateTo: UserPermissionsReducer<UserPermissionsScreen> = (state, { payload }) => ({
  ...state,
  screen: payload,
});

const handleSelectCurrentApp: UserPermissionsReducer<App> = (state, { payload }) => ({
  ...state,
  currentApp: payload,
  screen: UserPermissionsScreen.APP_ACCESS,
});

const handleSelectCurrentTracker: UserPermissionsReducer<App> = (state, { payload }) => ({
  ...state,
  currentTracker: payload,
  screen: UserPermissionsScreen.TRACKER_ACCESS,
});

const handleSelectCurrentPartner: UserPermissionsReducer<App> = (state, { payload }) => ({
  ...state,
  currentPartner: payload,
  screen: UserPermissionsScreen.PARTNER_ACCESS,
});

const handleResetState: UserPermissionsReducer = () => getInitialState();

const handleChangeAppPermissions: UserPermissionsReducer<AppRole> = (state, { payload }) => ({
  ...state,
  currentApp: {
    ...state.currentApp,
    access: {
      ...state.currentApp.access,
      role: payload,
    },
  },
  canApplyAppPermissions: true,
});

const handleChangeTrackerPermissions: UserPermissionsReducer<string> = (state, { payload }) => ({
  ...state,
  currentTracker: {
    ...state.currentTracker,
    access: {
      ...state.currentTracker.access,
      role: payload,
    },
  },
  canApplyAppPermissions: true,
});

const handleChangePartnerPermissions: UserPermissionsReducer<string> = (state, { payload }) => ({
  ...state,
  currentPartner: {
    ...state.currentPartner,
    access: {
      ...state.currentPartner.access,
      role: payload,
    },
  },
  canApplyAppPermissions: true,
});

const handleSaveUserPermissions: UserPermissionsReducer = state => ({
  ...state,
  isSubmitting: true,
});

const handleSaveAppPermissions: UserPermissionsReducer = state => {
  const byToken = app => app.token === state.currentApp.token;
  const appPermission = extractAppPermission({ app: state.currentApp });
  const result = {
    ...state,
    screen: UserPermissionsScreen.APP_LIST,
    canApplyUserPermissions: true,
  };
  const currentAppPermision = result.user.local_roles.mobile_apps.find(byToken);

  if (!currentAppPermision && appPermission.role !== 'off') {
    result.user.local_roles.mobile_apps.push(appPermission);
    return result;
  }

  if (appPermission.role === 'off') {
    const index = state.user.local_roles.mobile_apps.findIndex(byToken);
    if (index > -1) {
      result.user.local_roles.mobile_apps.splice(index, 1);
    }
    return result;
  }

  if (currentAppPermision) {
    currentAppPermision.role = appPermission.role;

    if (appPermission.role === 'custom') {
      currentAppPermision.partners = appPermission.partners;
      currentAppPermision.trackers = appPermission.trackers;
    } else {
      delete currentAppPermision.partners;
      delete currentAppPermision.trackers;
    }
  }

  return result;
};

const byToken =
  searchToken =>
  ({ token }) =>
    token === searchToken;

const handleSaveTrackerPermissions: UserPermissionsReducer = state => {
  const trackerPermission = extractTrackerPermission(state.currentTracker);

  if (!state.currentApp.access.trackers) {
    state.currentApp.access.trackers = [];
  }

  const prevTrackerPermission = state.currentApp.access.trackers.find(byToken(state.currentTracker.token));

  if (!prevTrackerPermission) {
    if (trackerPermission.role) {
      state.currentApp.access.trackers.push(trackerPermission);
    }
  } else {
    if (!trackerPermission.role) {
      const index = state.currentApp.access.trackers.findIndex(byToken(state.currentTracker.token));
      state.currentApp.access.trackers.splice(index, 1);
    } else {
      prevTrackerPermission.role = trackerPermission.role;
      if (trackerPermission.keywords) {
        prevTrackerPermission.keywords = trackerPermission.keywords;
      }
    }
  }

  return {
    ...state,
    screen: UserPermissionsScreen.APP_ACCESS,
    canApplyAppPermissions: true,
  };
};

const handleSavePartnerPermissions: UserPermissionsReducer = state => {
  const partnerPermission = extractPartnerPermission(state.currentPartner);

  if (!state.currentApp.access.partners) {
    state.currentApp.access.partners = [];
  }

  const previousPermissions = state.currentApp.access.partners.find(
    partner => String(partner.partner_id) === String(state.currentPartner.id),
  );

  if (!previousPermissions) {
    if (partnerPermission.role) {
      state.currentApp.access.partners.push(partnerPermission);
    }
  } else {
    if (!partnerPermission.role) {
      const index = state.currentApp.access.partners.findIndex(
        partner => String(partner.partner_id) === String(state.currentPartner.id),
      );
      state.currentApp.access.partners.splice(index, 1);
    } else {
      previousPermissions.role = partnerPermission.role;
    }
  }

  return {
    ...state,
    screen: UserPermissionsScreen.APP_ACCESS,
    canApplyAppPermissions: true,
  };
};

const handleSaveUserPermissionsFailed: UserPermissionsReducer = state => ({
  ...state,
  isAlertOpen: true,
  isSubmitting: false,
  trace: {
    status: TraceStatus.Error,
    message: t('error.an-error-occurred.contact-support'),
  },
});

const handleSaveUserPermissionsSuceed: UserPermissionsReducer = state => ({
  ...state,
  isAlertOpen: true,
  isSubmitting: false,
  trace: {
    status: TraceStatus.Info,
    message: t('users.user-update-success.message'),
  },
});

const handleUpdateTrackerKeywords: UserPermissionsReducer<string[]> = (state, { payload }) => ({
  ...state,
  currentTracker: {
    ...state.currentTracker,
    access: {
      ...state.currentTracker.access,
      keywords: payload || [],
    },
  },
});

const handleCreateUserSuceed: UserPermissionsReducer = state => ({
  ...state,
  isAlertOpen: true,
  trace: {
    status: TraceStatus.Info,
    message: t('users.user-create-success.message'),
  },
});

const handleCreateUserFailed: UserPermissionsReducer<string> = (state, { payload }) => ({
  ...state,
  isAlertOpen: true,
  trace: {
    status: TraceStatus.Error,
    message: t(payload),
  },
});

const handleCloseAlert: UserPermissionsReducer = state => ({
  ...state,
  isAlertOpen: false,
  trace: {
    status: TraceStatus.Info,
    message: '',
  },
});

const handleUpdateEmail: UserPermissionsReducer<string> = (state, { payload }) => ({
  ...state,
  user: {
    ...state.user,
    email: payload,
  },
});

const handleUpdateCurrentTab: UserPermissionsReducer<CustomUserPermissionTabs> = (state, { payload }) => ({
  ...state,
  currentTab: payload,
});

const handleEnableApplyAppPermissions: UserPermissionsReducer = state => ({
  ...state,
  canApplyAppPermissions: true,
});

const handleEnableSelectedPartners: UserPermissionsReducer<any[]> = (state, { payload }) => {
  const { partners = [] } = state.currentApp.access;
  const partnerPermissionList = payload.flatMap((partner: any) => {
    if (partner.selected) {
      return {
        partner_id: String(partner.id),
        role: 'reader',
      };
    } else {
      return [];
    }
  });

  const idList = new Set(partners.map(partner => partner.partner_id));
  const merged = [...partners, ...partnerPermissionList.filter(partner => !idList.has(partner.partner_id))];

  return {
    ...state,
    currentApp: {
      ...state.currentApp,
      access: {
        ...state.currentApp.access,
        partners: merged,
      },
    },
    canApplyAppPermissions: true,
  };
};

const handleDisableSelectedPartners: UserPermissionsReducer<any[]> = (state, { payload }) => {
  const idList = payload.flatMap(partner => (partner.selected ? String(partner.id) : []));
  const partnerPermissionList =
    state.currentApp.access.partners?.filter(partner => !idList.includes(partner.partner_id)) || [];

  return {
    ...state,
    currentApp: {
      ...state.currentApp,
      access: {
        ...state.currentApp.access,
        partners: partnerPermissionList,
      },
    },
    canApplyAppPermissions: true,
  };
};

const handleEnableSelectedTrackers: UserPermissionsReducer<any[]> = (state, { payload }) => {
  const { trackers = [] } = state.currentApp.access;
  const trackerPermissionList = payload.flatMap((tracker: any) => {
    if (tracker.selected) {
      return {
        token: String(tracker.token),
        role: 'reader',
      };
    } else {
      return [];
    }
  });

  const idList = new Set(trackers.map(tracker => tracker.token));
  const merged = [...trackers, ...trackerPermissionList.filter(tracker => !idList.has(tracker.token))];

  return {
    ...state,
    currentApp: {
      ...state.currentApp,
      access: {
        ...state.currentApp.access,
        trackers: merged,
      },
    },
    canApplyAppPermissions: true,
  };
};

const handleDisableSelectedTrackers: UserPermissionsReducer<any[]> = (state, { payload }) => {
  const idList = payload.flatMap(tracker => (tracker.selected ? String(tracker.token) : []));
  const trackerPermissionList =
    state.currentApp.access.trackers?.filter(tracker => !idList.includes(tracker.token)) || [];

  return {
    ...state,
    currentApp: {
      ...state.currentApp,
      access: {
        ...state.currentApp.access,
        trackers: trackerPermissionList,
      },
    },
    canApplyAppPermissions: true,
  };
};

export default handleActions<UserPermissionsState, any>(
  {
    [USER_FETCH_STARTED]: handleFetchUser,
    [USER_FETCH_SUCCEED]: handleFetchUserSucceed,
    [USER_FETCH_FAILED]: handleFetchUserFailed,
    [USER_CHANGE_ROLE]: handleChangeUserRole,
    [TOGGLE_FEATURE]: handleToggleFeature,
    [UPDATE_FEATURES]: handleUpdateFeatures,
    [NAVIGATE_TO]: handleNavigateTo,
    [RESET_STATE]: handleResetState,
    [SELECT_CURRENT_APP]: handleSelectCurrentApp,
    [SELECT_CURRENT_TRACKER]: handleSelectCurrentTracker,
    [SELECT_CURRENT_PARTNER]: handleSelectCurrentPartner,
    [CHANGE_APP_PERMISSIONS]: handleChangeAppPermissions,
    [CHANGE_TRACKER_PERMISSIONS]: handleChangeTrackerPermissions,
    [CHANGE_PARTNER_PERMISSIONS]: handleChangePartnerPermissions,
    [SAVE_USER_PERMISSIONS]: handleSaveUserPermissions,
    [SAVE_USER_PERMISSIONS_FAILED]: handleSaveUserPermissionsFailed,
    [SAVE_USER_PERMISSIONS_SUCEED]: handleSaveUserPermissionsSuceed,
    [SAVE_APP_PERMISSIONS]: handleSaveAppPermissions,
    [SAVE_TRACKER_PERMISSIONS]: handleSaveTrackerPermissions,
    [SAVE_PARTNER_PERMISSIONS]: handleSavePartnerPermissions,
    [UPDATE_TRACKER_KEYWORDS]: handleUpdateTrackerKeywords,
    [CREATE_USER_SUCCEED]: handleCreateUserSuceed,
    [CREATE_USER_FAILED]: handleCreateUserFailed,
    [CLOSE_ALERT]: handleCloseAlert,
    [UPDATE_EMAIL]: handleUpdateEmail,
    [UPDATE_CURRENT_TAB]: handleUpdateCurrentTab,
    [ENABLE_APPLY_APP_PERMISSIONS]: handleEnableApplyAppPermissions,
    [PARTNER_ENABLE_SELECTED]: handleEnableSelectedPartners,
    [PARTNER_DISABLE_SELECTED]: handleDisableSelectedPartners,
    [TRACKER_ENABLE_SELECTED]: handleEnableSelectedTrackers,
    [TRACKER_DISABLE_SELECTED]: handleDisableSelectedTrackers,
  },
  getInitialState(),
);

// Selectors

export const getUserPermissionsState = (state): UserPermissionsState => state.userPermissions;
